import { useState, useEffect, useRef } from "react";
import { ArrowUp, ArrowDown, Copy, Check, QrCode, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { castVote } from "../lib/api";
import { getFingerprint } from "../lib/fingerprint";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import type { Member } from "../shared/contracts";

interface Props {
  code: string;
  roomName: string | null;
  members: Member[];
  myMemberId: string;
  connected: boolean;
  generation: number;
  hasInit: boolean;
}

type VoteDirection = "up" | "down";

function MemberSkeleton() {
  return (
    <div className="flex items-center justify-between rounded-lg border px-4 py-3 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-4 h-3 bg-muted rounded" />
        <div className="space-y-1.5">
          <div className="w-24 h-3 bg-muted rounded" />
          <div className="w-10 h-2.5 bg-muted rounded" />
        </div>
      </div>
      <div className="flex gap-1.5">
        <div className="w-8 h-7 bg-muted rounded-md" />
        <div className="w-8 h-7 bg-muted rounded-md" />
      </div>
    </div>
  );
}

export function VotingBoard({ code, roomName, members, myMemberId, connected, hasInit }: Props) {
  const [myVotes, setMyVotes] = useState<Record<string, VoteDirection>>(() => {
    try {
      const stored = sessionStorage.getItem(`rateyou_votes_${code}_${myMemberId}`);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  const [voting, setVoting] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [voteError, setVoteError] = useState<string | null>(null);
  const [showQr, setShowQr] = useState(false);

  const prevMembersRef = useRef<Member[]>([]);
  const [listRef] = useAutoAnimate<HTMLDivElement>();

  // Persist votes to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(`rateyou_votes_${code}_${myMemberId}`, JSON.stringify(myVotes));
    } catch {
      // sessionStorage unavailable
    }
  }, [myVotes, code, myMemberId]);

  // Toast on new member joins (only after initial load)
  useEffect(() => {
    if (!hasInit) {
      prevMembersRef.current = members;
      return;
    }
    const prev = prevMembersRef.current;
    const newMembers = members.filter(m => !prev.some(p => p.id === m.id));
    for (const m of newMembers) {
      if (m.id !== myMemberId) toast(`${m.name} joined`);
    }
    prevMembersRef.current = members;
  }, [members, hasInit, myMemberId]);

  async function handleVote(targetId: string, direction: VoteDirection) {
    if (voting === targetId) return;
    setVoting(targetId);
    try {
      const fingerprint = await getFingerprint();
      await castVote(code, { targetId, direction, fingerprint });
      setMyVotes(v => {
        const next = { ...v };
        if (next[targetId] === direction) {
          delete next[targetId];
        } else {
          next[targetId] = direction;
        }
        return next;
      });
    } catch {
      setVoteError("Vote failed — tap again to retry.");
      setTimeout(() => setVoteError(null), 3000);
    } finally {
      setVoting(null);
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable
    }
  }

  const sorted = [...members].sort((a, b) => b.score - a.score);

  return (
    <div className="min-h-screen bg-background px-4 py-8">
      <div className="max-w-md mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">
              {roomName || "Room"}
            </h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">
                {code}
              </span>
              <span className="text-xs text-muted-foreground">
                · {members.length} {members.length === 1 ? "member" : "members"}
              </span>
              {!connected && (
                <span className="text-xs text-destructive">· reconnecting…</span>
              )}
            </div>
          </div>
          <div className="flex gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowQr(v => !v)}
              className="gap-1.5 text-xs"
              aria-label="Show QR code"
            >
              {showQr ? <X className="h-3 w-3" /> : <QrCode className="h-3 w-3" />}
            </Button>
            <Button variant="outline" size="sm" onClick={copyLink} className="gap-1.5 text-xs">
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? "Copied!" : "Invite"}
            </Button>
          </div>
        </div>

        {/* QR code panel */}
        {showQr && (
          <div className="flex flex-col items-center gap-2 rounded-lg border p-4">
            <QRCodeSVG value={window.location.href} size={160} />
            <p className="text-xs text-muted-foreground">Scan to join this room</p>
          </div>
        )}

        {/* Member list */}
        <div className="space-y-2" ref={listRef}>
          {!hasInit ? (
            <>
              <MemberSkeleton />
              <MemberSkeleton />
              <MemberSkeleton />
            </>
          ) : sorted.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Waiting for people to join…
            </p>
          ) : (
            sorted.map((member, i) => {
              const isMe = member.id === myMemberId;
              const currentVote = myVotes[member.id];

              return (
                <div
                  key={member.id}
                  className={cn(
                    "flex items-center justify-between rounded-lg border px-4 py-3 transition-opacity",
                    isMe && "opacity-50"
                  )}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs text-muted-foreground w-4 shrink-0">
                      {i + 1}
                    </span>
                    <div>
                      <p className="text-sm font-medium truncate">
                        {member.name}
                        {isMe && (
                          <span className="text-xs text-muted-foreground font-normal ml-1.5">(you)</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {member.upvotes > 0 && (
                          <span className="text-emerald-600">↑{member.upvotes}</span>
                        )}
                        {member.upvotes > 0 && member.downvotes > 0 && (
                          <span className="mx-0.5"> </span>
                        )}
                        {member.downvotes > 0 && (
                          <span className="text-red-500">↓{member.downvotes}</span>
                        )}
                        {member.upvotes === 0 && member.downvotes === 0 && (
                          <span>no votes yet</span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-1.5 shrink-0">
                    <button
                      onClick={() => !isMe && handleVote(member.id, "up")}
                      disabled={isMe || voting === member.id}
                      className={cn(
                        "rounded-md border px-2.5 py-1.5 text-xs transition-colors",
                        isMe
                          ? "cursor-not-allowed opacity-30 border-border"
                          : currentVote === "up"
                          ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                          : "hover:bg-emerald-50 hover:border-emerald-200 border-border"
                      )}
                      aria-label={`Upvote ${member.name}`}
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => !isMe && handleVote(member.id, "down")}
                      disabled={isMe || voting === member.id}
                      className={cn(
                        "rounded-md border px-2.5 py-1.5 text-xs transition-colors",
                        isMe
                          ? "cursor-not-allowed opacity-30 border-border"
                          : currentVote === "down"
                          ? "bg-red-50 border-red-300 text-red-600"
                          : "hover:bg-red-50 hover:border-red-200 border-border"
                      )}
                      aria-label={`Downvote ${member.name}`}
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {voteError && (
          <p className="text-xs text-destructive text-center">{voteError}</p>
        )}

        {hasInit && members.length < 3 && (
          <p className="text-xs text-muted-foreground text-center">
            Share the invite link so others can join and rate each other.
          </p>
        )}
      </div>
    </div>
  );
}
